import { Chip, Typography } from "@mui/material";
import { grey } from "@mui/material/colors";
import { Box, useTheme } from "@mui/system";
import { BigTooltip } from "components/BigTooltip";
import { federalWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";
import { RcraInfoFederalWasteGeneratorStatus } from "generated-graphql/graphql";
import { ReactNode, useMemo } from "react";
import {
  GeneratorStatusKind,
  getFederalGeneratorStatusColorSettings,
} from "./getFederalGeneratorStatusColorSettings";

const FederalGeneratorStatusTooltips: Record<
  RcraInfoFederalWasteGeneratorStatus,
  ReactNode
> = {
  [RcraInfoFederalWasteGeneratorStatus.VerySmallQuantityGenerator]: (
    <Box>
      <Typography variant="caption" fontStyle="italic">
        Very Small Quantity Generator (VSQG)
      </Typography>
      <br />
      <Typography variant="caption">
        Generated 220 lbs or less of hazardous waste per month [or combo of
        hazardous + acutely hazardous] and generated less than 2.2 lbs of
        acutely hazardous waste per month
      </Typography>
    </Box>
  ),
  [RcraInfoFederalWasteGeneratorStatus.SmallQuantityGenerator]: (
    <Box>
      <Typography variant="caption" fontStyle="italic">
        Small Quantity Generator (SQG)
      </Typography>
      <br />
      <Typography variant="caption">
        Generated more than 220 lbs and less than 2,200 lbs of hazardous waste
        per month [or combo of hazardous + acutely hazardous] and generated less
        than 2.2 lbs of acutely hazardous waste per month
      </Typography>
    </Box>
  ),
  [RcraInfoFederalWasteGeneratorStatus.LargeQuantityGenerator]: (
    <Box>
      <Typography variant="caption" fontStyle="italic">
        Large Quantity Generator (LQG)
      </Typography>
      <br />
      <Typography variant="caption">
        Generated more than 2,200 lbs of hazardous waste per month [or combo of
        hazardous + acutely hazardous] or generated more than 2.2 lbs of acutely
        hazardous waste per month
      </Typography>
    </Box>
  ),
  [RcraInfoFederalWasteGeneratorStatus.NotAGenerator]: null,
  [RcraInfoFederalWasteGeneratorStatus.Pending]: null,
  [RcraInfoFederalWasteGeneratorStatus.Undetermined]: null,
};

export type FederalGeneratorStatusIndicatorProps = {
  status: RcraInfoFederalWasteGeneratorStatus | null;
  type?: "circle" | "chip";
  active?: boolean;
  kind?: GeneratorStatusKind;
};

export function FederalGeneratorStatusIndicator({
  status,
  type = "chip",
  active,
  kind = GeneratorStatusKind.REPORTED,
}: FederalGeneratorStatusIndicatorProps) {
  const theme = useTheme();

  const infoTooltip = status ? FederalGeneratorStatusTooltips[status] : "";

  const { borderColor, chipColor } = useMemo(() => {
    return getFederalGeneratorStatusColorSettings(status, kind);
  }, [kind, status]);

  const border = useMemo(() => {
    return active
      ? `${type === "circle" ? "3px" : "2px"} solid ${borderColor}`
      : "none";
  }, [active, type, borderColor]);
  const textColor = active ? theme.palette.text.primary : grey[400];

  const childElement =
    type === "chip" ? (
      <Chip
        label={federalWasteGeneratorStatusToLabel(status)}
        size="small"
        variant="filled"
        clickable={false}
        sx={{
          width: "56px",
          height: "24px",
          backgroundColor: chipColor,
          border,
          color: textColor,
          cursor: "pointer",
        }}
      />
    ) : type === "circle" ? (
      <Box
        sx={{
          width: "56px",
          height: "56px",
          borderRadius: "50%",
          backgroundColor: chipColor,
          border,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <Typography variant="body2" color={textColor}>
          {federalWasteGeneratorStatusToLabel(status)}
        </Typography>
      </Box>
    ) : (
      <></>
    );

  return <BigTooltip title={infoTooltip}>{childElement}</BigTooltip>;
}
